import { Button, MiniProductReviews } from '@bepure/tarkin';
import * as React from 'react';
import {useState} from 'react';
import * as ReactDOM from 'react-dom';
import Slider from 'react-slick';

declare const BASE_API_URL: string;
declare const BASE_URL: string;

interface Props {
    reviews: {
        [id: number]: Review,
    };
    title?: string;
}

interface Review {
    id: string;
    name: string;
    email: string;
    score: number;
    title: string;
    description: string;
    sku: string;
    link?: string;
}

const ProductReviewCarousel = (props: Props) => {

    const numberOfReviews = Object.keys(props.reviews).length;
    const averageScore = (Object.keys(props.reviews).reduce((sum, key) => sum + parseFloat(props.reviews[key].score), 0) / numberOfReviews).toFixed(2);


    const slickMobileSettings = {
        dots: false,
        speed: 500,
        slidesToShow: 1,
        slidesToScroll: 1,
        swipeToSlide: true,
        arrows: true
    }
    const slickDesktopSettings = {
        dots: false,
        speed: 500,
        slidesToShow: numberOfReviews > 3 ? 4 : numberOfReviews,
        slidesToScroll: numberOfReviews > 3 ? 4 : numberOfReviews,
        swipeToSlide: true,
        arrows: true
    }
    

    return (
        <>
        <div className="product-reviews-section" id="product-reviews-section"></div>
        {
            numberOfReviews > 0 &&
            
            <div className="product-review-carousel">
            <h1 className="product-review-carousel__title">{props.title}</h1>
            <div className="product-review-carousel__star-container">
                <MiniProductReviews averageScore={5} noText={true} orange={true} />
                <div className="product-review-carousel__star-container__stats">
                    <div className="product-review-carousel__star-container__stats__average"><strong>{averageScore}</strong> average</div>
                    <div className="product-review-carousel__star-container__stats__count"><strong>{numberOfReviews}</strong> reviews</div>
                </div>
            <div className="product-review-carousel__write-review-mobile">
                <Button 
                text={"Leave a review"}
                ariaLabel={'write-a-review'}
                onClick={writeAReviewHandler}
                />
            </div>
            </div>
            <Slider {...slickMobileSettings} className="product-review-carousel--slick slick-mobile">
            {
                Object.keys(props.reviews).map((key, index) => {
                    return (
                        <ReviewSlide review={props.reviews[key]} id={key} key={key}/>
                    )
                })
            }
            </Slider>
            <Slider {...slickDesktopSettings} className="product-review-carousel--slick slick-desktop">
            {
                Object.keys(props.reviews).map((key, index) => {
                    return (
                        <ReviewSlide review={props.reviews[key]} id={key} key={key}/>
                    )
                })
            }
            </Slider>
        </div>
        }
        
        </>
    )
}

interface ReviewResponse {
    reviews: {
        [id: number]: {
            name: string;
            email: string;
            score: number;
            title: string;
            description: string;
            sku: string;
            link?: string;
        },
    };
}

const writeAReviewHandler = () => {
    window.location.href = 'reviews/?form=open'
}

const getAllReviews = async (sku?: string | null): Promise<ReviewResponse | null> => {
    const response = await fetch(`${BASE_API_URL}/v1/product/reviews`, {
        method: 'POST',
        headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            sku: sku,
        })
    });
    try {
        const content = await response.json();
        return Promise.resolve(content);
    } catch (error) {
        throw Promise.reject({
            error: error.message,
        });
    }
}

interface ProductPageV2 {
    id: number;
    meta: {
        type: string,
        detail_url: string,
        html_url: string,
        slug: string,
        first_published_at: string,
    };
    title: string;
    product_sku: string;
}

const getReviewLink = async (sku: string, productId?: string): Promise<string | null> => {
    const response = await fetch(`${BASE_URL}/api/v2/pages/?type=blog.ProductPageV2&fields=product_sku`, {
        method: 'GET',
        headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
        },
    });
    try {
        return await response.json().then(r => {
            const items: { [id: number]: ProductPageV2 } = r.items;
            const urls = Object.keys(items).map((key, idx) => items[key]).filter(item => item.product_sku ? item.product_sku === sku : false)
                         .map(item => BASE_URL + '/store/' + item.meta.slug + '/reviews?review_id=' + productId)
            return urls.length > 0 ? urls[0] : null
        })
        
    } catch (error) {
        throw Promise.reject({
            error: error.message
        });
    }
}

const truncateReview = (review: Review, url?: string): Review => {
    return {
        id: review.id,
        name: review.name,
        email: review.email,
        score: review.score,
        title: review.title,
        description: review.description.split(' ').slice(0, 35).join(' '),
        sku: review.sku,
        link: url ? url : undefined
    }
}

interface ReviewSlideProps {
    id: string;
    review: Review   
}

const ReviewSlide = (props: ReviewSlideProps) => {
    const [XDown, setXDown] = useState(0);
    const [YDown, setYDown] = useState(0);

    //checks and remembers coordinates on mousedown
    const mouseDownHandler = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        setXDown(e.clientX);
        setYDown(e.clientY);
    }

    //if the coordinates have changed by mouseup, it is a drag and doesnt count as clicking the review
    const mouseUpHandler = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        if(e.clientX !== XDown || e.clientY !== YDown){
            return;
        }
        if(props.review.link){
            window.location.href = props.review.link;
        }
    }

    return (
        <div className={`product-review-carousel__review ${props.review.link && ' product-review-carousel__clickable'}`} onMouseDown={mouseDownHandler} onMouseUp={mouseUpHandler}>
            <div className="product-review-carousel__review__meta">
                <div className="product-review-carousel__review__meta__name">{props.review.name}</div>
                <div className="product-review-carousel__review__meta__rating">
                    <MiniProductReviews averageScore={props.review.score} noText={true} orange={true} />
                </div>
            </div>
            <div className="product-review-carousel__review__title">{props.review.title}</div>
            <div className="product-review-carousel__review__description">
                {props.review.description}    
            </div>
            { props.review.link &&
                <div className="product-review-carousel__review__read-more"> 
                    <a href={props.review.link}>Read more</a>
                </div>
            }
        </div>
    )
}

export default function render() {
    const elements = document.getElementsByClassName('obiwan-product-review-carousel')
    Array.prototype.map.call(elements, (element: HTMLElement) => {
        const sku = element.dataset.sku !== 'None' ? element.dataset.sku : null
        getAllReviews(sku).then((r) => { 
            let reviews = r ? r.reviews : {}
            if (reviews) {
                const updatedReviews = Object.keys(reviews).map((key, idex) => reviews[key])
                .map(review => review.description.split(' ').length > 35 ?
                    getReviewLink(review.sku, review.id).then(url => {
                        return url ? truncateReview(review, url) : null // Don't show long reviews that can't be truncated
                    }) : review
                ).reverse();        
                const title = element.dataset.title;
                Promise.all(updatedReviews).then(reviews => {
                    return ReactDOM.render(
                        <ProductReviewCarousel
                            reviews={reviews.filter(r => r !==null)}
                            title={title}
                        />,
                        element
                    )
                })
            }
            
        }).catch((r) => {
            // tslint:disable-next-line: no-console
            console.log('Product Review Carousel Render Failed: ' + r)
        })
    })
}